import { aboutPostIdSetting, allowTypeIIIPlayerSetting, type ForumTypeString, siteUrlSetting, cloudinaryCloudName, commentPermalinkStyleSetting, crosspostKarmaThreshold, type3DateCutoffSetting, type3ExplicitlyAllowedPostIdsSetting, type3KarmaCutoffSetting } from '@/lib/instanceSettings';
import { getSiteUrl } from '../../vulcan-lib/utils';
import { userOwns, userCanDo, userOverNKarmaFunc, userIsAdminOrMod, userOverNKarmaOrApproved } from '../../vulcan-users/permissions';
import { userGetDisplayName, userIsSharedOn, type SharableDocument } from '../users/helpers';
import { postStatuses, postStatusLabels } from './constants';
import maxBy from "lodash/maxBy";
import { TupleSet, UnionOf } from '../../utils/typeGuardUtils';
import { ReviewYear, REVIEW_YEAR, getReviewPeriodStart, getReviewPeriodEnd } from '@/lib/reviewUtils';
import moment from 'moment';
import { isServer } from '@/lib/executionEnvironment';
import { getUrlClass } from '@/server/utils/getUrlClass';
import { captureException } from '@/lib/sentryWrapper';

export const postCategories = new TupleSet(['post', 'linkpost', 'question'] as const);
export type PostCategory = UnionOf<typeof postCategories>;
export const postDefaultCategory = 'post';
export const isPostCategory = (tab: string): tab is PostCategory => postCategories.has(tab)

//////////////////
// Link Helpers //
//////////////////

// Return a post's link if it has one, else return its post page URL
export const postGetLink = function (post: PostsBase|DbPost): string {
  if (post.url) {
    return post.url;
  }
  return postGetPageUrl(post);
};

export const postGetAbsoluteLink = (post: PostsBase|DbPost, forumType: ForumTypeString): string => {
  return post.url || postGetAbsolutePageUrl(post, forumType);
};

// Whether a post's link should open in a new tab or not
export const postGetLinkTarget = function (post: PostsBase|DbPost): string {
  return !!post.url ? '_blank' : '';
};

interface LinkpostDetectionResult {
  isLinkpost: boolean;
  linkpostDomain?: string;
}

// On the server, use the 'url' library for parsing hostname out of feed URLs.
// On the client, we instead create an <a> tag, set its href, and extract
// properties from that. (There is a URL class which theoretically would work,
// but it doesn't have the hostname field on IE11 and it's missing entirely on
// Opera Mini.)
const URLClass = getUrlClass()

export function getProtocol(url: string): string {
  if (isServer)
    return new URLClass(url).protocol;

  // From https://stackoverflow.com/questions/736513/how-do-i-parse-a-url-into-hostname-and-path-in-javascript
  var parser = document.createElement('a');
  parser.href = url;
  return parser.protocol;
}

export function getHostname(url: string): string {
  if (isServer)
    return new URLClass(url).hostname;

  // From https://stackoverflow.com/questions/736513/how-do-i-parse-a-url-into-hostname-and-path-in-javascript
  var parser = document.createElement('a');
  parser.href = url;
  return parser.hostname;
}

/**
 * Intended to be used when you have a url-like string that might be missing the protocol (http(s)://) prefix
 * Trying to parse those with `new URL()`/`new URLClass()` blows up, so this tries to correctly handle them
 * We default to logging an error to sentry and returning nothing if even that fails, but not confident we shouldn't just continue to throw in a visible way
 */
export function parseUnsafeUrl(url: string) {
  const urlWithProtocol = url.slice(0, 4) === 'http'
    ? url
    : `https://${url}`;

  try {
    const parsedUrl = new URLClass(urlWithProtocol);
    const protocol = getProtocol(urlWithProtocol);
    const hostname = getHostname(urlWithProtocol);
  
    return { protocol, hostname, parsedUrl };
  } catch (err) {
    captureException(`Tried to parse url ${url} as ${urlWithProtocol} and failed`);
  }

  return {};
}

// Detect if a post is a linkpost and get the domain
export const detectLinkpost = (
  post: { url?: string | null },
  rssFeedDomain?: string | null
): LinkpostDetectionResult => {
  if (!post.url) {
    return { isLinkpost: false };
  }

  const { hostname: linkpostDomain } = parseUnsafeUrl(post.url);
  
  if (rssFeedDomain) {
    const isLinkpost = linkpostDomain !== rssFeedDomain;
    return { isLinkpost, linkpostDomain };
  }

  return { isLinkpost: true, linkpostDomain };
};

///////////////////
// Other Helpers //
///////////////////

// Get a post author's name
export const postGetAuthorName = async function (post: DbPost, context: ResolverContext): Promise<string> {
  var user = await context.Users.findOne({_id: post.userId});
  if (user) {
    return userGetDisplayName(user, context.forumType);
  } else {
    return post.author ?? "[unknown author]";
  }
};

// Get default status for new posts.
export const postGetDefaultStatus = function (user: DbUser): number {
  return postStatuses.STATUS_APPROVED;
};

const findWhere = (array: any, criteria: any) => array.find((item: any) => Object.keys(criteria).every((key: any) => item[key] === criteria[key]));

// Get status name
export const postGetStatusName = function (post: DbPost): string {
  return findWhere(postStatusLabels, {value: post.status}).label;
};

// Check if a post is approved
export const postIsApproved = function (post: Pick<DbPost, '_id' | 'status'>): boolean {
  return post.status === postStatuses.STATUS_APPROVED;
};

// Get URL for sharing on Twitter.
export const postGetTwitterShareUrl = (post: DbPost, forumType: ForumTypeString): string => {
  return `https://twitter.com/intent/tweet?text=${ encodeURIComponent(post.title) }%20${ encodeURIComponent(postGetAbsoluteLink(post, forumType)) }`;
};

// Get URL for sharing on Facebook.
export const postGetFacebookShareUrl = (post: DbPost, forumType: ForumTypeString): string => {
  return `https://www.facebook.com/sharer/sharer.php?u=${ encodeURIComponent(postGetAbsoluteLink(post, forumType)) }`;
};

// Get URL for sharing by Email.
export const postGetEmailShareUrl = (post: DbPost, forumType: ForumTypeString): string => {
  const subject = `Interesting link: ${post.title}`;
  const body = `I thought you might find this interesting:

${post.title}
${postGetAbsoluteLink(post, forumType)}

(found via ${siteUrlSetting.get(forumType)})
  `;
  return `mailto:?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

const getSocialImagePreviewPrefix = (forumType: ForumTypeString) =>
  `https://res.cloudinary.com/${cloudinaryCloudName}/image/upload/c_fill,ar_1.91,g_auto/`;

// Select the social preview image for the post.
// For events, we use their event image if that is set.
// For other posts, we use the manually-set cloudinary image if available,
// or the auto-set from the post contents. If neither of those are available,
// it will return null.
export const getSocialPreviewImage = (post: DbPost, forumType: ForumTypeString): string => {
  // Note: in case of bugs due to failed migration of socialPreviewImageId -> socialPreview.imageId,
  // edit this to support the old field "socialPreviewImageId", which still has the old data
  const manualId = (post.isEvent && post.eventImageId) ? post.eventImageId : post.socialPreview?.imageId
  if (manualId) {
    return getSocialImagePreviewPrefix(forumType) + manualId;
  }
  const autoUrl = post.socialPreviewImageAutoUrl
  return autoUrl || ''
}

export const getSocialPreviewSql = (tablePrefix: string, forumType: ForumTypeString) => `JSON_BUILD_OBJECT(
  'imageUrl',
  CASE
    WHEN ${tablePrefix}."isEvent" AND ${tablePrefix}."eventImageId" IS NOT NULL
      THEN '${getSocialImagePreviewPrefix(forumType)}' || ${tablePrefix}."eventImageId"
    WHEN ${tablePrefix}."socialPreview"->>'imageId' IS NOT NULL
      THEN '${getSocialImagePreviewPrefix(forumType)}' || (${tablePrefix}."socialPreview"->>'imageId')
    ELSE COALESCE(${tablePrefix}."socialPreviewImageAutoUrl", '')
  END
)`;

// The set of fields required for calling postGetPageUrl. Could be supplied by
// either a fragment or a DbPost.
export interface PostsMinimumForGetPageUrl {
  _id: string
  slug: string
  isEvent?: boolean
  groupId?: string | undefined | null
}

// Get URL of a post page.
export const postGetPageUrl = function(post: PostsMinimumForGetPageUrl, sequenceId: string|null=null): string {
  // LESSWRONG – included event and group post urls
  if (sequenceId) {
    return `/s/${sequenceId}/p/${post._id}`;
  } else if (post.isEvent) {
    return `/events/${post._id}/${post.slug}`;
  } else if (post.groupId) {
    return `/g/${post.groupId}/p/${post._id}/`;
  }
  return `/posts/${post._id}/${post.slug}`;
};

export const postGetAbsolutePageUrl = (post: PostsMinimumForGetPageUrl, forumType: ForumTypeString, sequenceId: string|null=null): string => {
  return getSiteUrl(forumType).slice(0, -1) + postGetPageUrl(post, sequenceId);
};

export const postGetCommentsUrl = (
  post: PostsMinimumForGetPageUrl,
  sequenceId: string | null = null,
): string => {
  return postGetPageUrl(post, sequenceId) + "#comments";
}

export const postGetAbsoluteCommentsUrl = (post: PostsMinimumForGetPageUrl, forumType: ForumTypeString, sequenceId: string|null=null): string => {
  return postGetAbsolutePageUrl(post, forumType, sequenceId) + "#comments";
};

export const postGetEditUrl = (postId: string, linkSharingKey?: string, version?: string): string => {
  let url = `/editPost?postId=${postId}`;
  if (linkSharingKey) url += `&key=${linkSharingKey}`;
  if (version) url += `&version=${version}`;
  return url;
}

export const postGetAbsoluteEditUrl = (postId: string, forumType: ForumTypeString, linkSharingKey?: string, version?: string): string => {
  return getSiteUrl(forumType).slice(0, -1) + postGetEditUrl(postId, linkSharingKey, version);
};

export type PostWithCommentCounts = { commentCount: number; afCommentCount: number }
/**
 * Get the total (cached) number of comments, including replies and answers
 */
export const postGetCommentCount = (post: PostWithCommentCounts, forumType: ForumTypeString): number => {
  if (forumType === 'AlignmentForum') {
    return post.afCommentCount || 0;
  } else {
    return post.commentCount || 0;
  }
};

/**
 * Can pass in a manual comment count, or retrieve the post's cached comment count
 */
export const postGetCommentCountStr = (post: PostWithCommentCounts|null|undefined, forumType: ForumTypeString, commentCount?: number): string => {
  const count = commentCount !== undefined ? commentCount : post ? postGetCommentCount(post, forumType) : 0;
  if (!count) {
    return "No comments";
  } else if (count === 1) {
    return "1 comment";
  } else {
    return count + " comments";
  }
}

export const postGetAnswerCountStr = (count: number): string => {
  if (!count) {
    return "No answers";
  } else if (count === 1) {
    return "1 answer";
  } else {
    return count + " answers";
  }
}

export const getResponseCounts = ({ post, answers, forumType }: { post: PostWithCommentCounts; answers: CommentsList[]; forumType: ForumTypeString }) => {
  // answers may include some which are deleted:true, deletedPublic:true (in which
  // case various fields are unpopulated and a deleted-item placeholder is shown
  // in the UI). These deleted answers are *not* included in post.commentCount.
  const nonDeletedAnswers = answers.filter((answer) => !answer.deleted);

  const answerAndDescendentsCount =
    answers.reduce((prev: number, curr: CommentsList) => prev + curr.descendentCount, 0) + answers.length;

  return {
    answerCount: nonDeletedAnswers.length,
    commentCount: postGetCommentCount(post, forumType) - answerAndDescendentsCount,
  };
};

export const postGetLastCommentedAt = (post: PostsBase|DbPost, forumType: ForumTypeString): Date | null => {
  if (forumType === 'AlignmentForum') {
    return post.afLastCommentedAt ? new Date(post.afLastCommentedAt) : null;
  } else {
    return post.lastCommentedAt ? new Date(post.lastCommentedAt) : null;
  }
}

export const postGetLastCommentPromotedAt = (post: PostsBase|DbPost, forumType: ForumTypeString): Date|null => {
  if (forumType === 'AlignmentForum') return null
  // TODO: add an afLastCommentPromotedAt
  return post.lastCommentPromotedAt ? new Date(post.lastCommentPromotedAt) : null;
}

/**
 * Whether or not the given user is an organizer for the post's group
 * @param user
 * @param post
 * @returns {Promise} Promise object resolves to true if the post has a group and the user is an organizer for that group
 */
export const userIsPostGroupOrganizer = async (user: UsersMinimumInfo|DbUser|null, post: PostsBase|DbPost, context: ResolverContext): Promise<boolean> => {
  const { loaders } = context;

  const groupId = ('group' in post) ? post.group?._id : post.groupId;
  if (!user || !groupId)
    return false
    
  const group = await loaders.Localgroups.load(groupId);
  return !!group && group.organizerIds.some(id => id === user._id);
}

/**
 * Whether the user can make updates to the post document (including both the main post body and most other post fields)
 */
export const canUserEditPostMetadata = (currentUser: UsersCurrent|DbUser|null, post: SharableDocument & HasUserIdType & {
  group?: { organizerIds: string[] } | null;
}): boolean => {
  if (!currentUser) return false;

  const organizerIds = post.group?.organizerIds;
  const isPostGroupOrganizer = organizerIds ? organizerIds.some(id => id === currentUser?._id) : false;
  if (isPostGroupOrganizer) return true

  if (userOwns(currentUser, post)) return true
  if (userCanDo(currentUser, 'posts.edit.all')) return true
  // Shared as a coauthor? Always give access
  if ((post.coauthorUserIds ?? []).includes(currentUser._id)) {
    return true;
  }

  if (userIsSharedOn(currentUser, post) && post.sharingSettings?.anyoneWithLinkCan === "edit") return true 

  if (post.shareWithUsers?.includes(currentUser._id) && post.sharingSettings?.explicitlySharedUsersCan === "edit") return true 

  return false
}

export const postCanDelete = (currentUser: UsersCurrent|null, post: PostsBase): boolean => {
  if (userCanDo(currentUser, "posts.remove.all")) {
    return true
  }
  const organizerIds = post.group?.organizerIds;
  const isPostGroupOrganizer = organizerIds ? organizerIds.some(id => id === currentUser?._id) : false;
  return (userOwns(currentUser, post) || isPostGroupOrganizer) && !!post.draft
}

export const postGetKarma = (post: PostsBase|DbPost, forumType: ForumTypeString): number => {
  const baseScore = forumType === 'AlignmentForum' ? post.afBaseScore : post.baseScore
  return baseScore || 0
}

// User can add/edit the hideCommentKarma setting if:
//  1) The user is logged in and has the requisite setting enabled
//  And
//  2) The post does not exist yet
//  Or if the post does exist
//  3) The post doesn't have any comments yet
export const postCanEditHideCommentKarma = (user: UsersCurrent|DbUser|null, forumType: ForumTypeString, post?: PostWithCommentCounts|null): boolean => {
  return !!(user?.showHideKarmaOption && (!post || !postGetCommentCount(post, forumType)))
}

export type CoauthoredPost = NullablePartial<Pick<DbPost, "coauthorUserIds">>

export const userIsPostCoauthor = (user: UsersMinimumInfo|DbUser|null, post: CoauthoredPost): boolean => {
  if (!user) {
    return false;
  }
  return post.coauthorUserIds?.includes(user._id) ?? false;
}

export const isNotHostedHere = (post: PostsEdit|PostsEditQueryFragment|PostsPage|DbPost) => {
  return post?.fmCrosspost?.isCrosspost && !post?.fmCrosspost?.hostedHere
}

const mostRelevantTag = (
  tags: TagBasicInfo[],
  tagRelevance: Record<string, number>,
): TagBasicInfo | null => maxBy(tags, ({_id}) => tagRelevance[_id] ?? 0) ?? null;

export const postGetPrimaryTag = (post: PostsListWithVotes, includeNonCore = false) => {
  const {tags, tagRelevance} = post;
  const core = tags.filter(({core}) => core);
  const potentialTags = core.length < 1 && includeNonCore ? tags : core;
  const result = mostRelevantTag(potentialTags, tagRelevance);
  return typeof result === "object" ? result : undefined;
}

/**
 * Whether the post is allowed AI generated audio
 */
export const isPostAllowedType3Audio = (post: PostsWithNavigation|PostsWithNavigationAndRevision|PostsListWithVotes|DbPost, forumType: ForumTypeString): boolean => {
  if (!allowTypeIIIPlayerSetting.get(forumType)) return false

  try {
    const TYPE_III_DATE_CUTOFF = new Date(type3DateCutoffSetting.get(forumType))
    const TYPE_III_ALLOWED_POST_IDS = type3ExplicitlyAllowedPostIdsSetting.get(forumType)

    return (
      (new Date(post.postedAt) >= TYPE_III_DATE_CUTOFF ||
        TYPE_III_ALLOWED_POST_IDS.includes(post._id) ||
        post.baseScore > type3KarmaCutoffSetting.get(forumType) ||
        post.forceAllowType3Audio) &&
      !post.draft &&
      !post.authorIsUnreviewed &&
      !post.rejected &&
      !post.podcastEpisodeId &&
      !post.isEvent &&
      !post.question &&
      !post.debate &&
      !post.shortform &&
      post.status === postStatuses.STATUS_APPROVED
    );
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error(e)
    return false
  }
}

/**
 * Given a url like https://docs.google.com/document/d/1G4SNqovdoEHaHca20TPJA6D4Ck7Yo8ocvKdwdZdL5qA/edit#heading=h.82kaw9idgbpe
 * return just the id part (1G4SNqovdoEHaHca20TPJA6D4Ck7Yo8ocvKdwdZdL5qA in this case)
 */
export const extractGoogleDocId = (urlOrId: string): string | null => {
  const docIdMatch = urlOrId.match(/.*docs\.google\.com.*\/d\/(.+?)(\/|$)/);
  return docIdMatch ? docIdMatch[1] : null;
};

/**
 * Given a Google doc id like 1G4SNqovdoEHaHca20TPJA6D4Ck7Yo8ocvKdwdZdL5qA, return
 * the full url (as if you were able to edit it). In this case it would be
 * https://docs.google.com/document/d/1G4SNqovdoEHaHca20TPJA6D4Ck7Yo8ocvKdwdZdL5qA/edit
 */
export const googleDocIdToUrl = (docId: string): string => {
  return `https://docs.google.com/document/d/${docId}/edit`;
};

export const isRecombeeRecommendablePost = (post: Pick<DbPost, keyof PostsBase & keyof DbPost> | PostsBase, forumType: ForumTypeString): boolean => {
  // We explicitly don't check `isFuture` here, because the cron job that "publishes" those posts does a raw update
  // So it won't trigger any of the callbacks, and if we exclude those posts they'll never get recommended
  // `Posts.checkAccess` already filters out posts with `isFuture` unless you're a mod or otherwise own the post
  // So we're not really in any danger of showing those posts to regular users
  // TODO: figure out how to handle this more gracefully
  return !(
    post.shortform
    || post.unlisted
    || post.rejected
    || post.isEvent
    || !!post.groupId
    || post.disableRecommendation
    || post.status !== 2
    || post._id === aboutPostIdSetting.get(forumType)
  );
};

export const postIsPublic = (post: Pick<DbPost, '_id' | 'draft' | 'status'>) => {
  return !post.draft && post.status === postStatuses.STATUS_APPROVED
};

export type PostParticipantInfo = NullablePartial<Pick<PostsDetails, "userId"|"debate"|"coauthorUserIds">>;

export function isDialogueParticipant(userId: string, post: PostParticipantInfo) {
  if (post.userId === userId) return true 
  if (post.coauthorUserIds?.includes(userId)) return true
  return false
}

export type EditablePost = UpdatePostDataInput & {
  _id: string;
  tags: Array<TagBasicInfo>;
  autoFrontpage?: DbPost['autoFrontpage'];
  socialPreviewData: SocialPreviewType | null | undefined;
  user: PostsEdit['user'];
  commentCount: number;
  afCommentCount: number;
  contents: CreateRevisionDataInput & { html: string | null } | null;
  contents_latest: string | null;
  debate: boolean;
  title: string;
  myEditorAccess?: string;
} & Pick<PostsListBase, 'postCategory'>;

export interface PostSubmitMeta {
  redirectToEditor?: boolean;
  skipRedirect?: boolean;
  successCallback?: (editedPost: PostsEditMutationFragment) => void;
}

export const DEFAULT_QUALITATIVE_VOTE = 4;

export const MINIMUM_COAUTHOR_KARMA = 1;

export interface RSVPType {
  name: string;
  email: string;
  nonPublic: boolean;
  response: "yes" | "maybe" | "no";
  userId: string;
  createdAt: Date;
}

/**
 * Structured this way to ensure lazy evaluation of `crosspostKarmaThreshold` each time we check for a given user, rather than once on server start
 */
export const userPassesCrosspostingKarmaThreshold = (user: DbUser | UsersMinimumInfo | null, forumType: ForumTypeString) => {
  const currentKarmaThreshold = crosspostKarmaThreshold.get(forumType);

  return currentKarmaThreshold === null
    ? true
    : // userOverNKarmaFunc checks greater than, while we want greater than or equal to, since that's the check we're performing elsewhere

    // so just subtract one
    userOverNKarmaFunc(currentKarmaThreshold - 1)(user);
};

export function userCanEditCoauthors(user: UsersCurrent | null) {
  return userIsAdminOrMod(user) || userOverNKarmaOrApproved(MINIMUM_COAUTHOR_KARMA)(user);
}

export function isCollaborative(post: Pick<DbPost | PostsBase, '_id' | 'shareWithUsers' | 'sharingSettings' | 'collabEditorDialogue'>, fieldName: string): boolean {
  if (!post) return false;
  if (!post._id) return false;
  if (fieldName !== "contents") return false;
  if (!!post.shareWithUsers?.length) return true;
  if (post.sharingSettings?.anyoneWithLinkCan && post.sharingSettings.anyoneWithLinkCan !== "none")
    return true;
  if (post.collabEditorDialogue) return true;
  return false;
}

export const defaultVotingSystem = "namesAttachedReactions";

export const dateStr = (startDate?: Date) => startDate ? moment(startDate).format('YYYY-MM-DD') : '';

export function getDraftLabel(post: { draft?: boolean | null } | null) {
  if (!post) return "Save Draft";
  if (!post.draft) return "Move to Drafts";
  return "Save Draft";
}

export const allPostsParams = (reviewYear: ReviewYear = REVIEW_YEAR) => {
  const startDate = getReviewPeriodStart(reviewYear).toDate();
  const endDate = getReviewPeriodEnd(reviewYear).toDate();
  return { after: dateStr(startDate), before: dateStr(endDate), sortedBy: 'top', timeframe: 'yearly', frontpage: 'true', unnominated: 'true', limit: "100" };
};
